import { htmlCanvas, widget } from "@foretagsplatsen/widgetjs";
import jQuery from "jquery";
import MarketingPopupWidget from "../widgets/MarketingPopupWidget.js";
import responsive from "../responsive.js";
import theme from "../theme.js";
import viewportWidget from "./viewportWidget.js";

// Nico (2015/08/12): Introducing this class allows us to decouple
// the main chrome widgets responsible for the layout, and to
// centralize its handling, simplifying quite a bit the code.

/**
 * `applicationFrame` is responsible for handling the layout of
 * the application, as well as for keeping track of the current
 * selected document.
 *
 * `applicationFrame` is also the main widget of any monitor
 * application.
 *
 * @param {{}} [spec] - Object parameters
 * @param {widget} [spec.header] - Instance of header to use (no
 * will be used if not provided).
 * @param {widget} [spec.navigation] - Instance of navigation to use
 * (no navigation will be displayed if not proveded).
 * @param {widget} [spec.viewport] - Specify a specific instance of
 * viewport to use. If not provided, an instance of
 * `core/chrome/viewportWidget` is created.
 * @param {widget} [spec.actions] - Specify a specific instance of
 * actions widget to use. If not provided, the frame won't have
 * any right hand-side actions.
 * @param {{}} [my] - Protected properties
 * @return {*}
 */
function applicationFrame(spec, my) {
	spec ||= {};
	my ||= {};

	const that = widget(spec, my);

	my.viewport = spec.viewport || viewportWidget();
	my.header = spec.header;
	my.actions = spec.actions;
	my.wkHeader = spec.wkHeader;
	my.setCurrentLocale = spec.setCurrentLocale;

	my.marketingPopupWidget = new MarketingPopupWidget();

	my.navigation = spec.navigation;
	my.navigationHidden = false;

	if (my.actions) {
		my.header.onRightTogglerClick.register(() => {
			my.toggleActions();
		});
	}

	if (my.navigation) {
		my.header.onLeftTogglerClick.register(() => {
			my.toggleNavigation();
		});

		my.navigation.onIconModeToggled.register((isOpen) => {
			my.onNavigationToggled(isOpen);
		});

		if (my.wkHeader?.enabled) {
			my.wkHeader.widget.registerOnHamburgerIconClick(() =>
				my.navigation.toggleIconMode(),
			);

			my.wkHeader.widget.registerOnLogoutRequest(() =>
				my.redirectToLocationPath("/External/Authenticate/Logout"),
			);

			my.wkHeader.widget.registerOnLanguageChangeRequest(
				async (locale) => {
					await my.setCurrentLocale(locale);
					my.document.update();
				},
			);

			my.wkHeader.widget.registerOnOrganizationChangeRequest((event) => {
				const id = event.detail.id;
				my.redirectToLocationPath(`/Navigate/OrganizationalUnit/${id}`);
			});
		}
	}

	my.document = null;

	that.getDocument = function () {
		return my.document;
	};

	/**
	 * Set `document` as the current document of the application
	 * and perform the appropriate DOM updates.
	 */
	that.setDocument = function (document, hasMarketingLayer = false) {
		my.hasMarketingLayer = hasMarketingLayer;

		const body = jQuery("body");
		my.document = document;
		if (!my.document.hasNavigation()) {
			my.hideNavigation();
		}
		my.viewport.setDocument(document, hasMarketingLayer);
		if (my.actions) {
			if (responsive.isMobileOrIPadPortaitBreakpoint()) {
				my.hideActions();
			}
			my.actions.setDocument(document, hasMarketingLayer);
			my.onActionsToggled(!my.hasNoActions());
		}
		if (my.navigation) {
			my.onNavigationToggled(my.navigation.isIconMode());
		}
		if (my.header) {
			my.header.setDocument(document, hasMarketingLayer);
			my.setupScrolling(my.viewport);
		}

		if (hasMarketingLayer) {
			my.marketingPopupWidget.show(my.document.title);
		} else {
			my.marketingPopupWidget.kill();
		}

		// Perform DOM updates
		my.updatePageTitleFromDocument();
		body.removeClass();
		body.addClass(document.documentId);
		if (responsive.isStandalone()) {
			jQuery("body").addClass("standalone");
		}
		if (my.hasNoHeader()) {
			body.addClass("no-header");
		}
		jQuery("html, body").animate({ scrollTop: 0 }, 200);
	};

	that.toggleActions = function () {
		my.toggleActions();
	};

	that.renderContentOn = function (html) {
		if (my.wkHeader?.enabled) {
			html.render(my.wkHeader.widget);
		}
		if (my.header) {
			html.render(my.header);
		}
		if (my.navigation && !my.navigationHidden) {
			html.render(my.navigation);
		}
		if (my.actions) {
			html.render(my.actions);
		}
		html.render(my.viewport);
	};

	my.setupScrolling = function () {
		if (!my.header.getTitle) {
			return;
		}
		const originalTitle = my.header.getTitle();
		let originalTitleDisplayed = true;
		const threshold = 60;

		my.viewport.registerToScroll((jQueryViewport, document) => {
			const documentTitle = document.title;

			if (jQueryViewport.scrollTop() < threshold) {
				if (!originalTitleDisplayed) {
					my.header.setTitle(originalTitle);
					originalTitleDisplayed = true;
				}
			} else if (originalTitleDisplayed) {
				my.header.setTitle(documentTitle);
				originalTitleDisplayed = false;
			}
		});
	};

	my.updatePageTitleFromDocument = function () {
		htmlCanvas(jQuery("title")).render((html) => {
			const titles = [];

			if (theme.brandingName) {
				titles.push(theme.brandingName);
			}

			if (my.document.title) {
				titles.push(my.getTitleText());
			}

			if (titles.length > 0) {
				document.title = "";
				html.render(titles.join(" - "));
			}
		});
	};

	/**
	 * Return a sanitized version of the title of the current document, with
	 * no HTML markup, to be rendered in the head of the page.
	 */
	my.getTitleText = function () {
		const title = my.document.title;
		const fragment = jQuery(document.createDocumentFragment()).get(0);
		htmlCanvas(fragment).render(title);
		return jQuery(fragment).text();
	};

	my.hasNoHeader = function () {
		return !my.header || !my.header.isVisible();
	};

	my.hasNoActions = function () {
		return !my.actions || !my.actions.canBeDisplayed();
	};

	my.toggleNavigation = function () {
		my.navigation.toggleIconMode();
	};

	/**
	 * Update the viewport when the left menu (for navigation)
	 * is opened or closed.
	 *
	 * Possibly collapse the right-hand menu on mobile devices.
	 *
	 * @param {boolean} isIconMode - `true` when the navigation is in icon mode
	 */
	my.onNavigationToggled = function (isIconMode) {
		if (isIconMode) {
			my.viewport.addCssClass("leftmenu-sm-out");
			my.viewport.removeCssClass("leftmenu-lg-out");
		} else {
			my.viewport.addCssClass("leftmenu-lg-out");
			my.viewport.removeCssClass("leftmenu-sm-out");
		}

		if (my.actions && responsive.isMobileOrIPadPortaitBreakpoint()) {
			my.actions.hide();
			my.onActionsToggled(false);
		}
	};

	my.toggleActions = function () {
		const actionsOpen = my.actions.isOpen();
		if (responsive.isMobileOrIPadPortaitBreakpoint()) {
			my.navigation.setIconMode();
			my.onNavigationToggled(true);
		}

		if (actionsOpen) {
			my.actions.hide();
		} else {
			my.actions.show();
		}
		my.onActionsToggled(my.actions.isOpen());
	};

	my.hideActions = function () {
		if (!my.actions || !my.actions.isOpen()) {
			return;
		}
		my.toggleActions();
	};

	my.hideNavigation = function () {
		my.navigationHidden = true;
		that.update();
	};

	/**
	 * Update the viewport when the right menu (containing the actions)
	 * is opened or closed.
	 *
	 * @param {boolean} open - Indicates in the action menu in opened or not
	 */
	my.onActionsToggled = function (open) {
		my.viewport.handleActionsToggled(open);
	};

	// Init
	if (my.navigation) {
		// Setup the viewport for the initial icon mode of the
		// navigation.
		my.onNavigationToggled(true);
	}

	/**
	 * We listen to rotation changes on mobile/ipad and make sure
	 * that the actions menu is closed after rotating to avoid an
	 * empty actions menu to be displayed (like on the overview
	 * page).
	 */
	jQuery(window).on("orientationchange", () => {
		my.hideActions();
	});

	return that;
}

export default applicationFrame;
